import React, { useId, useRef } from "react";
import { Box, Button, FormHelperText, Grid, IconButton, Paper, Typography } from "@mui/material";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import CloseIcon from "@mui/icons-material/Close";

/**
 * Selector de fotos con vistas previas y botón para quitar cada una (crédito, cliente, garantía).
 *
 * @param {object} props
 * @param {{file: File, preview: string}[]} props.previews
 * @param {(e: React.ChangeEvent<HTMLInputElement>) => void} props.onChange Evento del input de archivos.
 * @param {(index: number) => void} props.onRemove
 * @param {string} [props.error] Texto de error junto al botón.
 * @param {boolean} [props.disabled]
 */
// El botón es un <button> de verdad que abre el input oculto. Antes era un <label> con rol de
// botón que contenía el input: un control dentro de otro (axe: nested-interactive).
export const SelectorFotos = ({ previews, onChange, onRemove, error, disabled }) => {
  const input = useRef(null);
  const idAyuda = useId();
  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
        <Button
          variant="outlined"
          color={error ? "error" : "primary"}
          disabled={disabled}
          startIcon={<AddPhotoAlternateOutlinedIcon />}
          aria-describedby={idAyuda}
          onClick={() => input.current && input.current.click()}
        >
          Elegir fotos
        </Button>
        <input
          ref={input}
          type="file"
          multiple
          accept="image/*"
          onChange={(e) => {
            onChange(e);
            // Sin esto, volver a elegir exactamente los mismos archivos no dispara el cambio.
            e.target.value = "";
          }}
          disabled={disabled}
          hidden
          tabIndex={-1}
          aria-hidden="true"
        />
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {previews.length === 0
            ? "Ninguna foto elegida"
            : previews.length === 1
            ? "1 foto elegida"
            : `${previews.length} fotos elegidas`}
        </Typography>
      </Box>
      {error ? (
        <FormHelperText id={idAyuda} error sx={{ mx: 0 }}>
          {error}
        </FormHelperText>
      ) : (
        <FormHelperText id={idAyuda} sx={{ mx: 0 }}>
          Al elegir de nuevo, las fotos anteriores se reemplazan.
        </FormHelperText>
      )}

      {previews.length > 0 && (
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {previews.map((preview, index) => (
            <Grid item xs={6} sm={4} md={3} key={`${preview.file.name}-${index}`}>
              <Paper
                sx={{
                  position: "relative",
                  p: 1,
                  height: 150,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <IconButton
                  size="small"
                  aria-label={`Quitar la foto ${index + 1}`}
                  disabled={disabled}
                  onClick={() => onRemove(index)}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    bgcolor: "background.paper",
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    "&:hover": { bgcolor: (theme) => theme.palette.soft.primary },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
                <img
                  src={preview.preview}
                  alt={`Vista previa ${index + 1}`}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                  onLoad={() => URL.revokeObjectURL(preview.preview)}
                />
              </Paper>
              <Typography
                variant="caption"
                display="block"
                textAlign="center"
                sx={{ mt: 0.5, color: "text.secondary", overflowWrap: "anywhere" }}
              >
                {preview.file.name}
              </Typography>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};
